import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { dayLabel, hardSetsByMuscleFromDraft, slotRefKey, type SlotRef } from "@heraion/shared";

import { EditorTopBar } from "../primitives/EditorTopBar";
import { useBreakpoint } from "../useBreakpoint";
import { useLanguageChip } from "../ProgramComposer/useLanguageChip";
import { PrescribeHeaderFields } from "../ProgramComposer/prescribe/PrescribeHeaderFields";
import { prescribeHeaderMeta } from "../ProgramComposer/prescribe/headerMeta";
import { PrescribeGlobalPanels } from "../ProgramComposer/prescribe/PrescribeGlobalPanels";
import { DayNavigator } from "./DayNavigator";
import { DayView } from "./DayView";
import { ProblemsBar, useDraftProblems } from "./ProblemsBar";
import { SelectionBar } from "./SelectionBar";
import { useDaySelection } from "./useDaySelection";
import { useV3SelectionActions } from "./useV3SelectionActions";
import { ExerciseView } from "./ExerciseView";
import type { ProgramEditorV3Props } from "./types";
import { useDraftLineOptions } from "./useDraftLineOptions";
import { useV3DayActions } from "./useV3DayActions";
import { useV3ExerciseActions } from "./useV3ExerciseActions";
import { RevealFocusedProvider, useRevealFocusedField } from "./useRevealFocusedField";
import { useV3Navigation } from "./useV3Navigation";
import { RoundLockBar } from "./RoundLockBar";
import { RuleSheet } from "./RuleSheet";
import { useRuleWords } from "./ruleWords";
import { useRoundLock } from "./useRoundLock";
import { useV3ProgressionActions } from "./useV3ProgressionActions";
import { makeV3Styles, SCROLL_END } from "./v3Styles";
import { WideEditorBody } from "./WideEditorBody";

export type { ProgramEditorV3Props } from "./types";

// The program editor (#336 slice 3a, brief §7.2), on the phone and on the
// web. Under 768 the phone's column; wider, WideEditorBody lays the same
// pieces out in zones (slice 6, brief §7.3). The phone, top to bottom:
// the pinned bar (back, language, save, undo/redo), the save's problems when
// a Save was refused (2B), then one scroller with the
// name and description, the save warnings, the day selector and heatmap, the
// day (or the open exercise with its round ladder), and the whole-program
// panels (publish, assignees, coverage); under it all, the selection bar
// while «Select» is on (2B). Read and written through the shell's
// Draft: no state of its own beyond where it is looking.
export function ProgramEditorV3(props: ProgramEditorV3Props) {
  const { theme, draft, setDraft, cellUnit, Picker, resolveName } = props;
  const { t } = useTranslation();
  const styles = useMemo(() => makeV3Styles(theme), [theme]);
  const { tier } = useBreakpoint();
  const wide = tier !== "narrow";
  // On a wide screen the exercise pane always shows one while the day has one.
  const nav = useV3Navigation(draft, wide);
  // The rounds already done in a program in progress are read only (slice 5):
  // every write of the tree below passes the lock.
  const lock = useRoundLock(props);
  const lockedThrough = lock.lockedThrough;
  const editing = { ...props, setDraft: lock.setDraft, structure: lock.structure };
  const { isRoundLocked } = lock;
  const days = useV3DayActions({ ...editing, nav, lockedThrough, isRoundLocked, refuse: lock.refuse });
  const exercises = useV3ExerciseActions({ ...editing, nav });
  const selection = useDaySelection(draft, nav);
  const selected = useV3SelectionActions({ ...editing, nav, selection, exercises });
  const problems = useDraftProblems(draft, cellUnit, props.error);
  const progression = useV3ProgressionActions({ ...editing, nav, lockedThrough, isRoundLocked });
  // The «Rule» sheet's targets: the open exercise, or the selection (slice 5).
  const [ruleFor, setRuleFor] = useState<SlotRef[] | null>(null);
  // Stable between renders, so the rule sheet's memos hold.
  const nameOf = useCallback(
    (r: SlotRef) => {
      const s = draft.sessions[r.day]?.slots[r.slot];
      return s ? resolveName(s.exerciseId, s.exerciseName) : "";
    },
    [draft, resolveName],
  );
  // The day's own name, its code when it has none.
  const dayNameOf = useCallback(
    (day: number) => draft.sessions[day]?.name.trim() || `D${day + 1}`,
    [draft],
  );
  const maxMeta = props.maxMeta;
  const maxOf = useCallback(
    (r: SlotRef) => {
      const s = draft.sessions[r.day]?.slots[r.slot];
      return s ? maxMeta?.get(s.exerciseId) : undefined;
    },
    [draft, maxMeta],
  );
  // A wide screen has no «Select»: one open below 768 closes on the way.
  const stopSelection = useRef(selection.stop);
  stopSelection.current = selection.stop;
  useEffect(() => {
    if (wide) stopSelection.current();
  }, [wide]);
  // The bar is gone: the toast goes back down.
  const onBottomBar = props.onBottomBar;
  useEffect(() => {
    if (!selection.active) onBottomBar?.(0);
  }, [selection.active, onBottomBar]);
  const lineOptions = useDraftLineOptions(cellUnit);
  const ruleWords = useRuleWords(lineOptions);
  const reveal = useRevealFocusedField(theme);
  const language = useLanguageChip({ theme, draft, setDraft });
  const hardSetsByMuscle = useMemo(
    () => hardSetsByMuscleFromDraft(draft, props.exerciseCatalog ?? []),
    [draft, props.exerciseCatalog],
  );
  // The host Picker, for adding to the day or replacing the open exercise.
  const [picking, setPicking] = useState<"add" | "replace" | null>(null);
  const daySlugs = (draft.sessions[nav.dayIdx]?.slots ?? []).map((s) => s.exerciseId);

  // The pieces both layouts place: the name and the save warnings above the
  // day, the whole-program panels below it, the day chips (the heatmap under
  // them below 768 only), and the open exercise, built once.
  const top = (
    <>
      <PrescribeHeaderFields
        theme={theme}
        name={draft.name}
        onChangeName={(v) => setDraft((d) => ({ ...d, name: v }))}
        description={draft.description}
        onChangeDescription={(v) => setDraft((d) => ({ ...d, description: v }))}
        meta={prescribeHeaderMeta(draft)}
        showLiveEditHint={props.showLiveEditHint}
      />
      {props.warningsPanel}
    </>
  );
  const navigator = (dayChips: boolean, heatmap = true) => (
    <DayNavigator
      theme={theme}
      dark={props.dark}
      draft={draft}
      nav={nav}
      days={days}
      confirm={props.confirm}
      dayChips={dayChips}
      heatmap={heatmap}
      maxMeta={props.maxMeta}
      cellUnit={cellUnit}
    />
  );
  const panels = (
    <View style={styles.gutter}>
      <PrescribeGlobalPanels
        theme={theme}
        hardSetsByMuscle={hardSetsByMuscle}
        publishBand={props.publishBand}
        assigneesBand={props.assigneesBand}
      />
    </View>
  );
  const exerciseView =
    nav.openSlotIdx === null ? null : (
      <ExerciseView
        // A fresh view per exercise: its note editor and menu start closed.
        key={draft.sessions[nav.dayIdx]?.slots[nav.openSlotIdx]?.id}
        theme={theme}
        styles={styles}
        draft={draft}
        nav={nav}
        slotIdx={nav.openSlotIdx}
        actions={exercises}
        lineOptions={lineOptions}
        cellUnit={cellUnit}
        resolveName={resolveName}
        onReplace={() => setPicking("replace")}
        tip={props}
        progression={progression}
        words={ruleWords}
        onOpenRule={setRuleFor}
        maxMeta={props.maxMeta}
        inPane={wide}
      />
    );

  return (
    <View style={local.container}>
      <EditorTopBar
        theme={theme}
        backLabel={t("program.backToList", { defaultValue: "Back to programs" })}
        onBack={props.onCancel}
        backIconOnly
        title=""
        center={language.trigger}
        onSave={() => {
          // A refused Save shows where; a clean one shows nothing.
          problems.arm();
          props.onSave();
        }}
        busy={props.busy}
        error={props.error}
        status={props.statusLine}
      />
      <ProblemsBar
        theme={theme}
        draft={draft}
        state={problems}
        error={props.error}
        resolveName={resolveName}
        onGo={nav.goToProblem}
      />
      <RoundLockBar theme={theme} styles={styles} lock={lock} />
      {language.tray}
      <RevealFocusedProvider value={reveal.onFieldFocus}>
        {wide ? (
          <WideEditorBody
            theme={theme}
            styles={styles}
            tier={tier}
            draft={draft}
            nav={nav}
            days={days}
            lineOptions={lineOptions}
            cellUnit={cellUnit}
            resolveName={resolveName}
            maxMeta={props.maxMeta}
            onAddExercise={() => setPicking("add")}
            writes={{ ...editing, undo: exercises.undo, isRoundLocked, refuse: lock.refuse, setCount: exercises.setRoundSets }}
            top={top}
            navigator={navigator(tier === "medium", false)}
            panels={panels}
            detail={exerciseView}
            detailScroll={reveal}
          />
        ) : (
          <ScrollView
            ref={reveal.scrollRef}
            style={local.body}
            // The keyboard's height as extra room at the end: the last fields can
            // then scroll above it even where the window does not shrink.
            contentContainerStyle={{ paddingBottom: SCROLL_END + reveal.keyboardHeight }}
            onScroll={reveal.onScroll}
            scrollEventThrottle={32}
            keyboardShouldPersistTaps="handled"
          >
            {top}
            {navigator(true)}
            {exerciseView ?? (
              <DayView
                theme={theme}
                styles={styles}
                draft={draft}
                nav={nav}
                days={days}
                lineOptions={lineOptions}
                resolveName={resolveName}
                onAddExercise={() => setPicking("add")}
                onAddToRound={(slotIdx, w) => exercises.setRoundActive(slotIdx, w, true)}
                selection={selection}
              />
            )}
            {panels}
          </ScrollView>
        )}
      </RevealFocusedProvider>
      {selection.active ? (
        <SelectionBar
          theme={theme}
          draft={draft}
          sIdx={nav.dayIdx}
          count={selection.selected.length}
          actions={selected}
          onRule={() => {
            const slots = draft.sessions[nav.dayIdx]?.slots ?? [];
            const refs = slots.flatMap((s, slot) => (selection.selected.includes(s.id) ? [{ day: nav.dayIdx, slot }] : []));
            if (refs.length > 0) setRuleFor(refs);
          }}
          onLayout={(e) => onBottomBar?.(e.nativeEvent.layout.height)}
        />
      ) : null}
      {ruleFor ? (
        <RuleSheet
          key={ruleFor.map(slotRefKey).join()}
          theme={theme}
          styles={styles}
          draft={draft}
          refs={ruleFor}
          lineOptions={lineOptions}
          scale={props.profile.effortScale ?? "rir"}
          words={ruleWords}
          nameOf={nameOf}
          dayOf={dayNameOf}
          lockedThrough={lockedThrough}
          maxOf={maxOf}
          onApply={(rules, rows) => progression.applyRule(rows, rules)}
          onClose={() => setRuleFor(null)}
        />
      ) : null}
      <Picker
        visible={picking !== null}
        onClose={() => setPicking(null)}
        presentSlugs={daySlugs}
        presentTreatment="mark-program"
        // PE-40: the Workout tab exists only with its handler (pickerProps).
        workouts={picking === "add" ? props.workouts : undefined}
        // The picker closes itself after an import.
        onAddWorkout={
          picking === "add" && props.workouts
            ? (plan) => exercises.importWorkout(plan, cellUnit)
            : undefined
        }
        workoutImportWeeks={draft.cycleWeeks}
        // #85: a day of another program, appended to the open day.
        programs={picking === "add" ? props.programs : undefined}
        onAddProgramDay={
          picking === "add" && props.programs
            ? (program, srcIdx) => exercises.importProgramDay(program, srcIdx, cellUnit)
            : undefined
        }
        // Flow 2: «In which days», on adds only; a replace stays in its day.
        addDays={
          picking === "add"
            ? {
                labels: draft.sessions.map((_, i) => `D${i + 1}`),
                a11yLabels: draft.sessions.map((_, i) => dayLabel(draft, i)),
                current: nav.dayIdx,
              }
            : undefined
        }
        onAddMany={(slugs, toDays) => {
          // Replace takes the first pick: the exercise keeps its prescription.
          if (picking === "replace" && nav.openSlotIdx !== null && slugs[0]) {
            exercises.replace(nav.openSlotIdx, slugs[0]);
          } else if (picking === "add") {
            exercises.add(slugs, toDays);
          }
          setPicking(null);
        }}
      />
    </View>
  );
}

const local = StyleSheet.create({
  container: { flex: 1 },
  body: { flex: 1 },
});
